import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isPaymentGateHeldError, paymentGateHeldDescription } from '../util/payment-gate';
import { MessageService } from './message.service';

const REASON_MAX_LENGTH = 300;

/** 서버 ErrorInfo.reason 은 any — 문자열 외에 오류 객체·{ groupId } 같은 식별자 객체도 온다. "[object Object]" 로 붙지 않게 한다. */
function describeReason(reason: unknown): string {
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object') {
    const { message } = reason as { message?: unknown };
    const text = typeof message === 'string' && message ? message : JSON.stringify(reason);
    return text.length > REASON_MAX_LENGTH ? `${text.slice(0, REASON_MAX_LENGTH)}…` : text;
  }
  return String(reason);
}

function nnbErrorDescription(body: { message: string; reason?: unknown }): string {
  return body.reason ? `${body.message}(${describeReason(body.reason)})` : body.message;
}

export class ErrorHttpInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private messageService: MessageService,
  ) {}

  private isOnLoginPage(): boolean {
    return window.location.pathname.replace(/\/+$/, '') === '/tabs/login';
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // 시스템 에러
        if (error.status === 502) {
          this.messageService.popup({
            description: '일시적인 오류가 발생하였습니다. 확인을 누르시면 페이지를 새로고침합니다.',
            confirmLabel: '확인',
            confirmBtnClass: 'primary',
            confirmHandler: () => {
              setTimeout(() => {
                window.location.reload();
              }, 500);
            },
            cancelLabel: '취소',
            isNested: true,
          });
          // 502는 위 팝업으로 처리하고 에러를 삼킨다 → catchError는 스트림을 반환해야 하므로 EMPTY
          return EMPTY;
        }
        if (error.status >= 500) {
          if (error.error?.type === 'NNB_ERROR_TYPE') {
            this.messageService.popup({
              title: '노는법 오류',
              description: nnbErrorDescription(error.error),
              cancelLabel: '확인',
              isNested: true,
            });
          } else {
            if (!!error.error) {
              // Extract message from error object if available
              const errorMessage =
                typeof error.error === 'string' ? error.error : error.error.message || error.message || error.error;
              this.messageService.popup({
                title: '노는법 오류',
                description: errorMessage,
                cancelLabel: '확인',
                isNested: true,
              });
            } else {
              this.messageService.popup({
                title: '노는법 오류',
                description: '알 수 없는 오류가 발생하였습니다. 문의 부탁드립니다.',
                cancelLabel: '확인',
                isNested: true,
              });
            }
          }
        }
        // 로그인 실패
        if (error.status === 401) {
          // href 부분 문자열로 보면 `/admin?returnUrl=/tabs/login` 같은 주소도 로그인 화면으로
          // 오인해 이동을 건너뛴다 — 사용자가 보호된 화면에 그대로 남는다. 경로만 비교한다.
          if (!this.isOnLoginPage()) {
            // 서버가 이 세션을 거부했다는 표시. 로그인 페이지가 이걸 보고 자동
            // 되돌림을 끊는다(login.page.ts) — 없으면 401이 무한 루프가 된다.
            this.router.navigate(['/tabs/login'], {
              queryParams: { returnUrl: this.router.url, sessionExpired: 1 },
            });
          }
        }
        // 권한 없음
        if (error.status === 403) {
          this.messageService.popup({
            title: '노는법 오류',
            description: '권한이 없습니다.',
            cancelLabel: '확인',
            isNested: true,
          });
        }
        // 결제 잠금만 여기서 띄운다. 다른 409 는 admin-banner 등 각 화면이 직접 안내한다.
        if (isPaymentGateHeldError(error)) {
          this.messageService.popup({
            title: '결제 처리 중',
            description: paymentGateHeldDescription(error.error.paymentId),
            cancelLabel: '확인',
            isNested: true,
          });
        }
        // 사용자 에러
        if (error.status === 400 || error.status === 404) {
          if (error.error?.type === 'NNB_ERROR_TYPE') {
            // Capacity exceeded error (error code 101) should be handled by the component
            // Skip interceptor popup for this specific error
            const isCapacityError = error.error.error?.id === 101;
            if (!isCapacityError) {
              this.messageService.popup({
                title: '오류',
                description: nnbErrorDescription(error.error),
                cancelLabel: '확인',
                isNested: true,
              });
            }
          }
        }
        return throwError(error);
      }),
    );
  }
}
